import { Auth0Client } from '@auth0/auth0-spa-js';

const UNAUTHENTICATED_AUTH0_ERRORS = new Set([
  'login_required',
  'consent_required',
  'missing_refresh_token',
]);

function getAuth0ErrorCode(error: unknown): string | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof error.error === 'string'
  ) {
    return error.error;
  }

  return undefined;
}

function isUnauthenticatedAuth0Error(error: unknown): boolean {
  const code = getAuth0ErrorCode(error);
  return code !== undefined && UNAUTHENTICATED_AUTH0_ERRORS.has(code);
}

/**
 * Resolves whether the user has a valid session on app bootstrap.
 * When Auth0 uses in-memory cache (staging/prod), `isAuthenticated()` is false
 * after F5 even though the SSO session is still valid — silent token restore fixes that.
 */
export async function resolveInitialAuthenticatedState(
  auth0Client: Auth0Client
): Promise<boolean> {
  if (await auth0Client.isAuthenticated()) {
    return true;
  }

  try {
    await auth0Client.getTokenSilently();
    return true;
  } catch (error) {
    if (isUnauthenticatedAuth0Error(error)) {
      return false;
    }

    throw error;
  }
}

/**
 * Same silent restore as bootstrap, but swallows transient errors.
 * Used when another tab logs in (BroadcastChannel) or this tab is focused
 * while still showing the login form — memory-cache `isAuthenticated()` is false.
 */
export async function tryRestoreAuthenticatedSession(
  auth0Client: Auth0Client
): Promise<boolean> {
  try {
    return await resolveInitialAuthenticatedState(auth0Client);
  } catch {
    return false;
  }
}

export type TabResumeAuthAction = 'wait' | 'restore' | 'pushToken';

/**
 * While `isAuthenticated` is undefined, initAuth0 still owns silent restore.
 * Do not start a second getTokenSilently / bootstrap from tab focus.
 */
export function getTabResumeAuthAction(
  isAuthenticated: boolean | undefined
): TabResumeAuthAction {
  if (isAuthenticated === undefined) {
    return 'wait';
  }
  if (isAuthenticated === false) {
    return 'restore';
  }
  return 'pushToken';
}
